import React, { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ApiCallService from "../../../Service/ApiCallService";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import ReactTable from "../../../components/common/ReactTable";

const TABS = {
  NEW_CHAPTER: 0,
  EDIT_CHAPTER: 1,
};

const ChapterList = () => {
  const [chapterRequests, setChapterRequests] = useState([]);
  const [memberRequests, setMemberRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.NEW_CHAPTER);
  const [selectedMemberRequest, setSelectedMemberRequest] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatLocation = (row) => {
    const location = row?.location || row?.chapterCreatedForLocation;
    if (!location) return "N/A";

    const parts = [
      location.village?.villageName,
      location.taluka?.talukaName,
      location.district?.districtName,
      location.region?.regionName,
      location.state?.stateName,
    ].filter(Boolean);

    return parts.join(", ") || "N/A";
  };

  const isEditChapterRequest = (row) => {
    const requestType = String(row?.requestType || "")
      .toUpperCase()
      .trim();
    const existingOrNew = String(row?.excelMeta?.existingOrNew || "")
      .toLowerCase()
      .trim();

    return (
      requestType === "EDIT" ||
      Boolean(row?.liveChapter) ||
      ["edit", "update", "existing"].includes(existingOrNew)
    );
  };

  const fetchNewChapterRequests = async () => {
    const response = await ApiCallService.get(
      `${process.env.REACT_APP_BASE_URL}/getNotApprovedChapters?page=1&limit=200`,
    );
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    setChapterRequests(rows);
  };

  const fetchEditChapterRequests = async () => {
    const response = await ApiCallService.get(
      `${process.env.REACT_APP_BASE_URL}/chapter-requests?type=EDIT&status=PENDING&page=1&limit=200`,
    );
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    setChapterRequests(rows);
  };

  const fetchMemberRequests = async () => {
    const response = await ApiCallService.get(
      `${process.env.REACT_APP_BASE_URL}/chapter-member-requests`,
    );
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];
    setMemberRequests(rows);
  };

  const fetchActiveTabData = async () => {
    try {
      setLoading(true);
      if (activeTab === TABS.EDIT_CHAPTER) {
        await Promise.all([fetchEditChapterRequests(), fetchMemberRequests()]);
      } else {
        await fetchNewChapterRequests();
      }
    } catch (error) {
      console.error("Error fetching verification data:", error);
      message.error("Failed to fetch verification data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behaviour: "smooth" });
  }, []);

  useEffect(() => {
    fetchActiveTabData();
  }, [activeTab]);

  const filteredChapterRequests = useMemo(() => {
    if (activeTab === TABS.NEW_CHAPTER) {
      return chapterRequests.filter((row) => !isEditChapterRequest(row));
    }
    if (activeTab === TABS.EDIT_CHAPTER) {
      return chapterRequests.filter((row) => isEditChapterRequest(row));
    }
    return [];
  }, [activeTab, chapterRequests]);

  const filteredMemberRequests = useMemo(() => {
    return memberRequests.filter((row) => row?.status === "PENDING");
  }, [memberRequests]);

  const unifiedEditRows = useMemo(() => {
    if (activeTab !== TABS.EDIT_CHAPTER) return filteredChapterRequests;

    const editRows = [...filteredChapterRequests];
    const existingLiveChapterIds = new Set(
      editRows
        .map((row) =>
          typeof row?.liveChapter === "object" ? row?.liveChapter?._id : row?.liveChapter,
        )
        .filter(Boolean)
        .map((v) => String(v)),
    );

    const grouped = filteredMemberRequests.reduce((acc, req) => {
      const chapterId =
        typeof req?.chapter === "object" ? req?.chapter?._id : req?.chapter;
      if (!chapterId) return acc;
      const key = String(chapterId);
      if (existingLiveChapterIds.has(key)) return acc;
      if (!acc[key]) {
        acc[key] = {
          _id: `member-only-${key}`,
          chapterId: key,
          chapterName: req?.chapter?.chapterName || "N/A",
          status: "PENDING",
          createdAt: req?.requestedAt,
          __kind: "MEMBER_ONLY",
          locationText: "N/A",
        };
      } else {
        const currentDate = new Date(acc[key].createdAt || 0).getTime();
        const nextDate = new Date(req?.requestedAt || 0).getTime();
        if (nextDate && (!currentDate || nextDate < currentDate)) {
          acc[key].createdAt = req?.requestedAt;
        }
      }
      return acc;
    }, {});

    return [...editRows, ...Object.values(grouped)];
  }, [activeTab, filteredChapterRequests, filteredMemberRequests]);

  const handleVerifyChapterRequest = (requestId) => {
    navigate(`/chapter/verify/${requestId}`, {
      state: { source: "chapterRequest" },
    });
  };

  const handleMemberRequestAction = async (requestId, action) => {
    try {
      setLoading(true);
      const endpoint =
        action === "approve"
          ? `${process.env.REACT_APP_BASE_URL}/chapter-member-requests/${requestId}/approve`
          : `${process.env.REACT_APP_BASE_URL}/chapter-member-requests/${requestId}/reject`;

      await ApiCallService.post(endpoint, {
        reason: action === "reject" ? "Declined by reviewer" : "",
      });
      message.success(
        action === "approve"
          ? "Member change request approved"
          : "Member change request rejected",
      );
      await fetchMemberRequests();
    } catch (error) {
      console.error("Error processing member request:", error);
      message.error("Failed to process member request");
    } finally {
      setLoading(false);
    }
  };

  const getMemberRequestDiff = (row) => {
    const action = row?.action || "";
    const currentRole = row?.currentRole?.name || "-";
    const requestedRole = row?.requestedRole?.name || "-";

    if (action === "REQUESTED_ADD") {
      return {
        label: "Role Assignment",
        previous: "-",
        next: requestedRole,
      };
    }

    if (action === "REQUESTED_REMOVE") {
      return {
        label: "Role Assignment",
        previous: currentRole,
        next: "-",
      };
    }

    return {
      label: "Role Assignment",
      previous: currentRole,
      next: requestedRole,
    };
  };

  const chapterRequestColumns = [
    {
      field: "chapterName",
      headerName: "Chapter Name",
      renderCell: (row) => row?.chapterName || row?.proposedChapterName || "N/A",
    },
    {
      field: "location",
      headerName: "Location",
      renderCell: (row) => row?.locationText || formatLocation(row),
      disableSort: true,
    },
    {
      field: "status",
      headerName: "Status",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => (
        <Chip
          size="small"
          color="warning"
          label={row?.status || "PENDING"}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created On",
      renderCell: (row) => formatDate(row?.createdAt),
      disableSearch: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => (
        <Button
          variant="contained"
          sx={{
            backgroundColor: theme.palette.secondary.figma,
            "&:hover": { backgroundColor: theme.palette.primary.main },
          }}
          onClick={() => {
            if (row?.__kind === "MEMBER_ONLY") {
              navigate(`/chapter/verify/${row?.chapterId}`, {
                state: { memberOnlyReview: true, source: "memberRequest" },
              });
              return;
            }
            handleVerifyChapterRequest(row?._id);
          }}
        >
          View / Verify
        </Button>
      ),
    },
  ];

  const memberRequestColumns = [
    {
      field: "chapter",
      headerName: "Chapter",
      renderCell: (row) => row?.chapter?.chapterName || "N/A",
    },
    {
      field: "member",
      headerName: "Member",
      renderCell: (row) =>
        `${row?.user?.userFirstName || ""} ${row?.user?.userLastName || ""}`.trim() ||
        "N/A",
    },
    {
      field: "mobile",
      headerName: "Mobile",
      renderCell: (row) => row?.user?.mobileNumber || "N/A",
      disableSearch: true,
    },
    {
      field: "action",
      headerName: "Request Type",
      renderCell: (row) => row?.action || "N/A",
      disableSearch: true,
    },
    {
      field: "currentRole",
      headerName: "Current Role",
      renderCell: (row) => row?.currentRole?.name || "-",
      disableSearch: true,
    },
    {
      field: "requestedRole",
      headerName: "Requested Role",
      renderCell: (row) => row?.requestedRole?.name || "-",
      disableSearch: true,
    },
    {
      field: "requestedAt",
      headerName: "Requested On",
      renderCell: (row) => formatDate(row?.requestedAt),
      disableSearch: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      disableSort: true,
      disableSearch: true,
      renderCell: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelectedMemberRequest(row)}
          >
            View Changes
          </Button>
          {/* <Button
            size="small"
            variant="contained"
            onClick={() => handleMemberRequestAction(row?._id, "approve")}
          >
            Approve
          </Button> */}
          {/* <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleMemberRequestAction(row?._id, "reject")}
          >
            Reject
          </Button> */}
        </Box>
      ),
    },
  ];

  const title =
    activeTab === TABS.NEW_CHAPTER
      ? "New Chapters Verification"
      : "Chapter Edit Requests Verification";

  return (
    <Paper elevation={0} variant="pageBg">
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          // background: theme.palette.background.default,
        }}
      >
        <Typography
          variant={{xs:"h5", sm:"h4"}}
          sx={{ fontWeight: "bold", alignContent: "center", mb: 0 }}
        >
          {title}
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        variant="scrollable"
        onChange={(_, next) => setActiveTab(next)}
        sx={{ mb: 2 }}
      >
        <Tab label="New Chapters" />
        <Tab label="Chapter Edit Requests" />
      </Tabs>
      <ReactTable
        columns={chapterRequestColumns}
        rows={activeTab === TABS.EDIT_CHAPTER ? unifiedEditRows : filteredChapterRequests}
        rowKey="_id"
        loading={loading}
      />

      <Dialog
        open={Boolean(selectedMemberRequest)}
        onClose={() => setSelectedMemberRequest(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          Member Change Review
          <IconButton onClick={() => setSelectedMemberRequest(null)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMemberRequest && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Chapter
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedMemberRequest?.chapter?.chapterName || "N/A"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Member
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {`${selectedMemberRequest?.user?.userFirstName || ""} ${selectedMemberRequest?.user?.userLastName || ""}`.trim() || "N/A"}
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Request Type
                </Typography>
                <Chip label={selectedMemberRequest?.action || "N/A"} />
              </Box>

              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "#FFE58F",
                  backgroundColor: "#FFFBE6",
                  borderRadius: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Changed Field
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getMemberRequestDiff(selectedMemberRequest).label}
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  <span
                    style={{ color: "#9CA3AF", textDecoration: "line-through" }}
                  >
                    {getMemberRequestDiff(selectedMemberRequest).previous}
                  </span>
                  <span style={{ margin: "0 8px" }}>-</span>
                  <span style={{ color: "#15803D", fontWeight: 600 }}>
                    {getMemberRequestDiff(selectedMemberRequest).next}
                  </span>
                </Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Requested On
                </Typography>
                <Typography>{formatDate(selectedMemberRequest?.requestedAt)}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedMemberRequest && (
            <>
              <Button
                color="error"
                variant="outlined"
                onClick={async () => {
                  await handleMemberRequestAction(selectedMemberRequest?._id, "reject");
                  setSelectedMemberRequest(null);
                }}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  await handleMemberRequestAction(selectedMemberRequest?._id, "approve");
                  setSelectedMemberRequest(null);
                }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ChapterList;
